import React from 'react';
import { View } from 'react-native';
import { useRouter } from 'expo-router';
import Icon from '@/components/Icon';
import Logo from '@/components/Icons/Logo';
import Text from '@/components/Typography/Text';
import { CtaRow, DisplayHeading, GhostCta, Lede, PrimaryCta, useLanding } from './LandingKit';
import { Card, ContentSection } from './MarketingKit';
import { openUseCaseDraft } from '@/lib/openUseCaseDraft';
import { eventName, MARKETING_ACTION, trackMarketingEvent } from '@/constants/utils/analyticsEvents';
import { revealSection } from './landingInteractionHelpers';
export { scrollBehavior, carouselEdges, carouselStep, revealSection } from './landingInteractionHelpers';

export const fileIcon = name => /\.(csv|xlsx)$/.test(name) ? 'table' : /\.pdf$/.test(name) ? 'file-pdf' : /\.(js|html)$/.test(name) ? 'code' : /\.pptx$/.test(name) ? 'file-powerpoint' : 'file-lines';
export function VisualFile({ file }) {
  return <span className={`ucs-visual-file ucs-file-type-${fileIcon(file.name)}`}><span className="ucs-visual-file-icon"><Icon name={fileIcon(file.name)} size={18} /></span><span><strong>{file.name}</strong><small>Example input</small></span></span>;
}
export function Action({ children, prompt, mode = 'work', modelSlug, secondary = false, label }) {
  const router = useRouter();
  const actionLabel = React.Children.toArray(children).join('');
  return <View style={{ marginTop: secondary ? 16 : 0, alignSelf: secondary ? 'stretch' : 'center' }}>
    <PrimaryCta label={children} accessibilityLabel={label || actionLabel} full={secondary} restrained={secondary}
      tone={secondary ? 'secondary' : undefined} style={secondary ? { height: 'auto', minHeight: 44, paddingHorizontal: 14, paddingVertical: 10 } : undefined}
      onPress={() => {
      const slug = typeof window === 'undefined' ? '' : window.location.pathname.match(/^\/use-cases\/([a-z0-9-]+)$/)?.[1];
      trackMarketingEvent(MARKETING_ACTION.CTA_CLICKED, eventName('use-cases', slug, `${mode}_draft`, 'app'));
      openUseCaseDraft(router, { prompt, mode, ...(modelSlug ? { modelSlug } : {}) }, { preserveThroughSignIn: true });
      }} />
  </View>;
}
export function ProductFrame({ name, children, kind = 'file-lines', mode = 'work' }) {
  return <div className="ucs-product-frame"><div className="ucs-frame-bar"><span className="ucs-brand"><Logo width={18} height={18} color="currentColor" /><strong>Gab AI</strong><span className="ucs-work-label">{mode === 'work' ? 'Work' : 'Chat'}</span></span><span className="ucs-sample-label">Sample preview</span></div><div className="ucs-artifact-name"><Icon name={kind} size={17} /><span>{name}</span></div>{children}</div>;
}

export function ShowcaseHeader({ demo, copy = demo, children }) {
  const { c, display, isXS } = useLanding();
  return <ContentSection tag="header" className="ucs-hero" style={{ alignItems: 'center', paddingTop: isXS ? 92 : 112 }}>
    <GhostCta label="Use cases" href="/use-cases" icon="arrow-left" style={{ marginBottom: 24 }} />
    <DisplayHeading tag="h1" mask={false} lines={[demo.headline]}
      type={display({ min: 38, max: 64, leading: 1.05, tracking: -0.04 })}
      style={{ maxWidth: 1100, textAlign: 'center', textWrap: 'balance' }} />
    <Lede max={630} style={{ textAlign: 'center', textWrap: 'balance', marginTop: 22, marginBottom: 26 }}>{demo.intro}</Lede>
    <CtaRow style={{ justifyContent: 'center', flexWrap: 'wrap' }}><Action prompt={demo.prompt} mode={demo.mode} modelSlug={demo.modelSlug}>{copy.primaryCta}</Action>
      <GhostCta label={copy.secondaryCta} href="#product-demo" icon="arrow-down" onPress={event => revealSection(event, 'product-demo')} />
    </CtaRow>
    <Text tag="p" size="xs" color={c.muted} style={{ marginTop: 13 }}>Gab AI Work requires Plus and uses credits.</Text>
    {children}
  </ContentSection>;
}

export function ShowcaseBenefits({ name, items }) {
  const { c, isCompact } = useLanding();
  return <ContentSection aria-label={`Ways Gab AI helps ${name.toLowerCase()}`} style={{ paddingTop: 40, paddingBottom: 20 }}>
    <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: 20 }}>
      {items.map(item => <Card tag="article" key={item.title} style={{ flex: 1, gap: 13, alignItems: 'flex-start' }}>
        <Icon name={item.icon} size={23} color={c.ink} /><Text tag="h2" size="xl" weight="bold" color={c.ink}>{item.title}</Text>
        <Text tag="p" size="n" color={c.muted} style={{ lineHeight: 23 }}>{item.description}</Text>
        <GhostCta label="Learn more" href={item.href} icon="arrow-up-right" style={{ marginTop: 'auto' }} />
      </Card>)}
    </View>
  </ContentSection>;
}

export function ShowcaseTrust({ trust }) {
  const { c, isCompact } = useLanding();
  return <ContentSection title={trust.title} headingId="ucs-trust-title" aria-labelledby="ucs-trust-title">
    <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: 28, marginTop: 30 }}>
      {trust.items.map(item => <Card tag="article" key={item.title} style={{ flex: 1, gap: 13, alignItems: 'flex-start' }}>
        <Icon name={item.icon} size={22} color={c.ink} /><Text tag="h3" size="xl" weight="bold" color={c.ink}>{item.title}</Text>
        <Text tag="p" size="n" color={c.muted} style={{ lineHeight: 23 }}>{item.description}</Text>
        <GhostCta label={item.linkLabel} href={item.href} icon="arrow-up-right" />
      </Card>)}
    </View>
  </ContentSection>;
}

export function ShowcaseClosing({ demo, copy = demo, relatedUseCases }) {
  const { c } = useLanding();
  return <>
    <ContentSection title={copy.final.title} style={{ alignItems: 'center', borderTopWidth: 1, borderColor: c.rule, marginTop: 40 }}>
      <Lede size="sm" style={{ textAlign: 'center', marginTop: 20, marginBottom: 24 }}>{copy.final.description}</Lede>
      <Action prompt={demo.prompt} mode={demo.mode} modelSlug={demo.modelSlug}>{copy.primaryCta}</Action>
      <Text tag="p" size="xs" color={c.muted} style={{ maxWidth: 650, textAlign: 'center', marginTop: 13 }}>{demo.handoff}</Text>
    </ContentSection>
    <ContentSection tag="nav" aria-label="Related use cases" style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 24, paddingTop: 22, paddingBottom: 40 }}>
      <GhostCta label="All use cases" href="/use-cases" />
      {relatedUseCases.filter(Boolean).map(item => <GhostCta key={item.slug} label={item.name} href={`/use-cases/${item.slug}`} />)}
    </ContentSection>
  </>;
}
